import { AfterViewInit, Component, ElementRef, Inject, Injector, OnDestroy, PLATFORM_ID, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterLink } from '@angular/router';
import { runAfterHydration } from '../shared/run-after-hydration';
import { initPageScrollReveal } from '../shared/scroll-reveal.util';

interface ClientLogoItem {
  name: string;
  file: string;
}

interface ClientProjectItem {
  title: string;
  category: string;
  description: string;
  image: string;
  route: string;
  featured?: boolean;
}

@Component({
  selector: 'app-clients',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './clients.component.html',
  styleUrl: './clients.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class ClientsComponent implements AfterViewInit, OnDestroy {
  @ViewChild('clientsRoot') clientsRoot!: ElementRef<HTMLElement>;

  readonly logos: ClientLogoItem[] = [
    { name: 'BBVA', file: 'bbva.png' },
    { name: 'Carajillo', file: 'carajillo.png' },
    { name: 'Delantal', file: 'delantal.png' },
    { name: 'ExxonMobile', file: 'exxonmobile.png' },
    { name: 'IMSS', file: 'imms.png' },
    { name: 'Universal', file: 'universal.png' },
    { name: 'IOS Offices', file: 'ios_offices.png' },
    { name: 'Loreal', file: 'loreal.png' },
    { name: 'Moderna', file: 'moderna.png' },
    { name: 'Panamericana', file: 'panamericana.png' },
    { name: 'Pernod Ricard', file: 'pernod_ricard.png' },
    { name: 'Quaker', file: 'quaker.png' },
    { name: 'Qualcomm', file: 'qualcomm.png' },
    { name: 'Savinon', file: 'savinon.png' },
    { name: 'Sophia', file: 'sophia.png' },
    { name: 'TEDx', file: 'tedx.png' },
    { name: 'Tuborg', file: 'tuborg.png' }
  ];

  readonly projects: ClientProjectItem[] = [
    {
      title: 'Liverpool',
      category: 'Digital Fidelity Strategy',
      description: 'Estrategia digital para fidelización, comunicación y crecimiento comercial.',
      image: 'assets/images/projects/portfolio/liverpool.jpg',
      route: '/clientes/liverpool',
      featured: true
    },
    {
      title: 'Carajillo',
      category: 'Marketing Strategy',
      description: 'Planeación de marketing para posicionar experiencias gastronómicas y acelerar demanda.',
      image: 'assets/images/projects/portfolio/carajillo.jpg',
      route: '/clientes/carajillo',
      featured: true
    },
    {
      title: 'Citibanamex',
      category: 'Retail Marketing Strategy',
      description: 'Estrategia retail para campañas, comunicación y activaciones orientadas a negocio.',
      image: 'assets/images/projects/portfolio/citibanamex.jpg',
      route: '/clientes/citibanamex',
      featured: true
    },
    {
      title: 'Pernod Ricard',
      category: 'Marketing Strategy',
      description: 'Presencia y conexión comercial para marcas de consumo.',
      image: 'assets/images/projects/portfolio/pernod-ricard.png',
      route: '/clientes/pernod-ricard'
    },
    {
      title: 'Jaloma',
      category: 'Retail Marketing Strategy',
      description: 'Visibilidad, canales y comunicación de producto para retail.',
      image: 'assets/images/projects/portfolio/jaloma.png',
      route: '/clientes/jaloma'
    },
    {
      title: 'Football Legends',
      category: 'Marketing Business Development',
      description: 'Desarrollo comercial, marketing y presencia de marca.',
      image: 'assets/images/projects/portfolio/football-legends.png',
      route: '/clientes/football-legends'
    },
    {
      title: 'Organizer Star/HBO',
      category: 'Streaming Website',
      description: 'Experiencia digital orientada a contenido y consumo en streaming.',
      image: 'assets/images/projects/portfolio/organizer-star.png',
      route: '/clientes/organizer-star'
    },
    {
      title: 'Estrella Real',
      category: 'Launching Marketing Strategy',
      description: 'Lanzamiento de marca para generar awareness y activar demanda.',
      image: 'assets/images/projects/portfolio/estrella-real.png',
      route: '/clientes/estrella-real'
    },
    {
      title: 'La Texana',
      category: 'Retail Marketing Strategy',
      description: 'Comunicación, presencia comercial y posicionamiento retail.',
      image: 'assets/images/projects/portfolio/la-texana.png',
      route: '/clientes/la-texana'
    },
    {
      title: 'Metaown',
      category: 'App Development e Integración AI',
      description: 'Producto digital con app e integración de inteligencia artificial.',
      image: 'assets/images/projects/portfolio/metaown.png',
      route: '/clientes/metaown'
    },
    {
      title: 'Evry',
      category: 'Ecommerce Development',
      description: 'Ecommerce para experiencia de compra, operación digital y conversión.',
      image: 'assets/images/projects/portfolio/evry.png',
      route: '/clientes/evry'
    },
    {
      title: 'Pixel Mining',
      category: 'App Development',
      description: 'Aplicación digital con enfoque en producto, experiencia y escalabilidad.',
      image: 'assets/images/projects/portfolio/pixel-mining.png',
      route: '/clientes/pixel-mining'
    }
  ];

  private gsapCleanups: Array<() => void> = [];

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private injector: Injector
  ) {}

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      runAfterHydration(this.injector, () => void this.setupScrollRevealAnimation());
    }
  }

  ngOnDestroy(): void {
    this.gsapCleanups.forEach((cleanup) => cleanup());
    this.gsapCleanups = [];
  }

  get featuredProjects(): ClientProjectItem[] {
    return this.projects.filter((project) => project.featured);
  }

  get secondaryProjects(): ClientProjectItem[] {
    return this.projects.filter((project) => !project.featured);
  }

  private async setupScrollRevealAnimation(): Promise<void> {
    const root = this.clientsRoot?.nativeElement;
    if (!root) return;
    await initPageScrollReveal(root, this.gsapCleanups);
  }
}
